import { Location } from '@angular/common';
import { AfterViewInit, ChangeDetectorRef, Component, ComponentFactoryResolver, ComponentRef, HostListener, OnDestroy, OnInit, Type, ViewChild, ViewContainerRef } from '@angular/core';
import * as get from 'lodash/get';
import { Subject } from 'rxjs';

import { DialogConfig } from '../models/dialog-config.model';


/**
 *
 *
 * @export
 * @class DialogComponent
 * @description DialogComponent: is the dialog shell which the template component will be handled via a componentRef. This container component
 * contains the html, and css for the modal shell.
 */
@Component({
	selector: 'ac-dialog',
	templateUrl: './dialog.component.html',
	styleUrls: ['./dialog.component.scss']
})

export class DialogComponent implements OnInit, AfterViewInit, OnDestroy {

	private readonly _onClose = new Subject<any>();
	public onClose = this._onClose.asObservable();
	public childComponentType: Type<any>;
	public componentRef: ComponentRef<any>;
	public showClose: boolean = false;
	public closeAnalyticsEvent: string;
	public overrideStyles: Object = {};
	@ViewChild('dialogTemplate', { read: ViewContainerRef, static: true }) public insertionPoint: ViewContainerRef;

	constructor(
		private componentFactoryResolver: ComponentFactoryResolver,
		private changeDetector: ChangeDetectorRef,
		private location: Location
	) { }

	public ngOnInit(): void {

		// If the URL changes for some reason (router navigation or back button is clicked) we should close the dialog
		this.location.onUrlChange((url, state) => {
			this.close();
		});
	}

	public ngAfterViewInit(): void {

		// This will trigger change detection after our child component is actually loaded
		// this is necessary to avoid expression changed error:
		// https://indepth.dev/everything-you-need-to-know-about-the-expressionchangedafterithasbeencheckederror-error/
		this.loadChildComponent(this.childComponentType);
		this.changeDetector.detectChanges();
	}

	/**
	 * onDialogClicked: event handler for click event on the dialog container.
	 */
	public onDialogClicked(evt: Event): void {
		evt.stopPropagation();
	}

	public close(): void {
		this._onClose.next(null);
	}


	/**
	 * @description loadChildComponent: using the componentFactoryResolver the template component is instantiated and loaded.
	 * @param {Type<any>} componentType: the template component.
	 */
	public loadChildComponent(componentType: Type<any>): void {
		const componentFactory = this.componentFactoryResolver.resolveComponentFactory(componentType);

		this.insertionPoint.clear();
		this.componentRef = this.insertionPoint.createComponent(componentFactory);

		const config: DialogConfig = get(this.componentRef, ['instance', 'config']);
		if (config) {
			this.showClose = config.showClose;
			this.overrideStyles = config.style;
			this.closeAnalyticsEvent = get(config, 'data.closeAnalyticsEvent', 'dialog:button:close');
		}
	}

	/**
	 * @description ngOnDestroy: handles the destruction of the componentRef: template component.
	 */
	public ngOnDestroy(): void {

		// if we've loaded a child comp let's make certain to destroy it
		if (this.componentRef) {
			this.componentRef.destroy();
		}
	}

	@HostListener('document:keydown.escape', ['$event'])
	public onKeydownHandler(event: KeyboardEvent): void {
		this.close();
	}
}

